import ReactGA from 'react-ga4'
import { CookieConsent } from './CookieConsent'

/**
 * Google Analytics(GA4) 전송을 한 곳에 모은다. 페이지뷰는 RouteChangeTracker, 이벤트는 각 화면이 부른다.
 *
 * - **동의한 방문자만.** react-ga4 는 초기화 전 호출도 dataLayer 에 쌓아 두었다가 스크립트가 뜨면
 *   한꺼번에 보낸다. 동의 전 이벤트가 동의 순간 새어 나가지 않도록 호출마다 동의를 먼저 확인한다.
 * - **자동 page_view 는 끈다**(`send_page_view: false`). 페이지뷰는 경로가 바뀔 때 수동으로 한 번만 보낸다.
 *   GA 관리 화면의 향상된 측정 "브라우저 기록 이벤트 기반 페이지 변경"도 꺼야 이동마다 두 번 잡히지 않는다.
 * - **page_location 에서 쿼리를 걷어낸다.** `/sso?sso=&sig=`(Discourse 서명)·`?accessToken=` 이 Google 로
 *   넘어가지 않게 한다. 유입 집계에 쓰는 utm_* 와 Contact 의 topic 만 남긴다.
 * - **관리자·SSO 경로는 보내지 않는다.** 그 화면에 있는 동안은 `ga-disable-{ID}` 로 향상된 측정(스크롤·
 *   외부 링크)까지 멈춘다.
 *
 * **Google Tag Manager(광고 업체 태그)** 도 여기서 올린다 (ANALYTICS §11). `REACT_APP_GTM_ID` 가 비면 아무것도 안 한다.
 * - GA 와 같은 동의·같은 제외 경로. 동의 전·제외 경로로 첫 진입하면 컨테이너를 올리지 않는다.
 * - GTM 은 한번 올라오면 내릴 수 없다. 동의를 철회하거나 백오피스로 들어가면 새로고침으로 내린다.
 * - dataLayer 에는 정해 둔 이벤트만 민다(`GTM_EVENT_LIST`). 품목은 코드·이름만 — 수량·단가를 빼서
 *   주문 하나에 몇 대가 팔렸는지 업체로 새지 않게 한다. 금액(value)은 광고 성과 계산에 필요해 싣는다.
 * - GA4 는 이 코드가 보낸다. GTM 안에 GA4 태그를 또 만들면 두 번 잡힌다.
 */

const MEASUREMENT_ID: string = process.env.REACT_APP_GOOGLE_ANALYTICS ?? ''
const GTM_ID: string = process.env.REACT_APP_GTM_ID ?? ''

/** 집계하지 않는 경로 (접두사 일치) */
const EXCLUDED_PATH_LIST = ['/main', '/admin', '/auth', '/sso']

/**
 * GTM 이 이미 올라온 상태로 들어가면 새로고침해 내리는 경로. `/auth`·`/sso` 는 늘 전체 로드로 들어온다
 * (`window.location.assign` · 포럼 리다이렉트) — `/sso` 를 새로고침하면 Discourse 서명을 다시 쓰게 된다.
 */
const GTM_UNLOAD_PATH_LIST = ['/main', '/admin']

/** dataLayer 로 내보내는 이벤트. 여기 없는 이벤트(다운로드·매뉴얼 검색·로그인)는 GA 로만 간다 */
const GTM_EVENT_LIST = [
	'page_view',
	'view_item',
	'add_to_cart',
	'begin_checkout',
	'purchase',
	'generate_lead',
	'sign_up',
]

/** GA4 전자상거래 모양(`ecommerce` 객체)으로 내보내는 이벤트 */
const GTM_ECOMMERCE_EVENT_LIST = [
	'view_item',
	'add_to_cart',
	'begin_checkout',
	'purchase',
]

/** page_location 에 남기는 쿼리. 나머지는 버린다 */
const KEPT_QUERY_LIST = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'topic',
]

let isInitialized = false
let isGtmLoaded = false
let lastPathname: string | null = null

const matchesPrefix = (pathname: string, pathList: string[]): boolean =>
	pathList.some(aPath => pathname === aPath || pathname.startsWith(`${aPath}/`))

const isExcludedPath = (pathname: string): boolean =>
	matchesPrefix(pathname, EXCLUDED_PATH_LIST)

const currentPageLocation = (): string => {
	const { origin, pathname, search } = window.location
	const params = new URLSearchParams(search)
	const kept = new URLSearchParams()

	KEPT_QUERY_LIST.forEach(aKey => {
		const value = params.get(aKey)
		if (value !== null) kept.set(aKey, value)
	})

	const query = kept.toString()
	return `${origin}${pathname}${query === '' ? '' : `?${query}`}`
}

/** GA 공식 차단 스위치. 켜 두면 이미 올라온 gtag 도 아무것도 보내지 않는다 */
const setMeasurementDisabled = (disabled: boolean) => {
	const flagWindow = window as unknown as Record<string, boolean>
	flagWindow[`ga-disable-${MEASUREMENT_ID}`] = disabled
}

/**
 * `_ga`·`_ga_*` 쿠키를 지운다. gtag 는 쓸 수 있는 가장 넓은 도메인(.judahs.io)에 쓰므로
 * 호스트의 모든 상위 도메인으로 한 번씩 지운다 — 맞지 않는 도메인은 브라우저가 무시한다.
 */
const removeGaCookies = () => {
	const nameList = document.cookie
		.split(';')
		.map(aPair => aPair.split('=')[0].trim())
		.filter(aName => aName === '_ga' || aName.startsWith('_ga_'))

	if (nameList.length === 0) return

	const labelList = window.location.hostname.split('.')
	const domainList = ['', ...labelList.map((_, idx) => labelList.slice(idx).join('.'))]

	nameList.forEach(aName => {
		domainList.forEach(aDomain => {
			const domainPart = aDomain === '' ? '' : `; domain=${aDomain}`
			document.cookie = `${aName}=; Max-Age=0; path=/${domainPart}`
		})
	})
}

const pushDataLayer = (entry: Record<string, unknown>) => {
	const layerWindow = window as unknown as { dataLayer?: Record<string, unknown>[] }
	layerWindow.dataLayer = layerWindow.dataLayer ?? []
	layerWindow.dataLayer.push(entry)
}

/** 공식 설치 스니펫과 같은 일을 한다. index.html 에 넣지 않는 이유 = 동의 전에 올라오면 안 된다 */
const loadGtm = () => {
	if (GTM_ID === '' || isGtmLoaded) return

	pushDataLayer({ 'gtm.start': Date.now(), event: 'gtm.js' })
	const script = document.createElement('script')
	script.async = true
	script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`
	document.head.appendChild(script)
	isGtmLoaded = true
}

/** 품목에서 코드·이름·옵션만 남긴다 (수량·단가 제외 — 맨 위 주석) */
const toGtmItemList = (itemList: unknown): Record<string, unknown>[] =>
	Array.isArray(itemList)
		? itemList.map(anItem => ({
				item_id: anItem.item_id,
				item_name: anItem.item_name,
				...(anItem.item_variant !== undefined && { item_variant: anItem.item_variant }),
		  }))
		: []

const pushGtm = (name: string, params: Record<string, unknown>) => {
	if (!isGtmLoaded || !GTM_EVENT_LIST.includes(name)) return

	if (!GTM_ECOMMERCE_EVENT_LIST.includes(name)) {
		pushDataLayer({ event: name, ...params })
		return
	}

	const { items, page_location, ...rest } = params
	// 앞 이벤트의 ecommerce 값이 dataLayer 에 남아 섞이지 않게 비운다 (GTM 권장)
	pushDataLayer({ ecommerce: null })
	pushDataLayer({
		event: name,
		page_location,
		ecommerce: { ...rest, items: toGtmItemList(items) },
	})
}

/** 보내도 되는 상태면 true. 처음 true 가 되는 순간 GA 를 초기화하고 GTM 을 올린다 */
const ensureReady = (): boolean => {
	if ((MEASUREMENT_ID === '' && GTM_ID === '') || !CookieConsent.isGranted()) return false

	const isExcluded = isExcludedPath(window.location.pathname)
	if (MEASUREMENT_ID !== '') setMeasurementDisabled(isExcluded)
	if (isExcluded) return false

	loadGtm()

	if (MEASUREMENT_ID !== '' && !isInitialized) {
		ReactGA.initialize(MEASUREMENT_ID, { gtagOptions: { send_page_view: false } })
		// page_location 을 config 에 넣지 않는다 — gtag 는 config 값을 set 보다 우선해서, 넣으면 자동 이벤트
		// (user_engagement·스크롤)가 첫 페이지 주소에 묶인다(2026-09-15 실측). set 으로만 갱신한다.
		ReactGA.gtag('set', { page_location: currentPageLocation() })
		isInitialized = true
	}

	return true
}

const send = (name: string, params: Record<string, unknown> = {}) => {
	if (!ensureReady()) return

	// 빈 값은 싣지 않는다 — GA 가 "(not set)" 대신 빈 문자열 행을 만든다
	const cleanParams = Object.keys(params).reduce<Record<string, unknown>>((acc, aKey) => {
		const value = params[aKey]
		if (value !== undefined && value !== '') acc[aKey] = value
		return acc
	}, {})

	// 이벤트는 페이지 effect 에서 불려 RouteChangeTracker 보다 먼저 나갈 수 있다 — 위치를 직접 싣는다
	const located = { ...cleanParams, page_location: currentPageLocation() }
	if (isInitialized) ReactGA.gtag('event', name, located)
	pushGtm(name, located)
}

const round2 = (n: number): number => Math.round(n * 100) / 100

/** 장바구니 한 줄. CartType 과 같은 모양이되 필요한 필드만 */
interface AnalyticsItemType {
	productCode: string
	productName: string
	seasonUsd: number
	qty: number
	option?: { name: string }
}

/** KRW 결제는 품목 단가(USD)를 싣지 않는다 — GA 는 품목 가격을 이벤트 통화로 읽는다 */
const toGaItem = (anItem: AnalyticsItemType, withPrice: boolean) => ({
	item_id: anItem.productCode,
	item_name: anItem.productName,
	quantity: anItem.qty,
	...(withPrice && { price: anItem.seasonUsd }),
	...(anItem.option !== undefined && { item_variant: anItem.option.name }),
})

export type AnalyticsDownloadEventType =
	| 'download_software'
	| 'download_manual'
	| 'download_device_sheet'

export const Analytics = {
	/**
	 * 페이지뷰. RouteChangeTracker 가 경로가 바뀔 때 부른다.
	 * 같은 경로를 연달아 받으면 무시한다 — 동의를 철회했다 다시 누르면 같은 페이지가 두 번 잡힌다.
	 */
	pageview: (pathname: string) => {
		if (pathname === lastPathname) return
		lastPathname = pathname

		if (!ensureReady()) return

		const pageLocation = currentPageLocation()
		// 향상된 측정 이벤트(스크롤·외부 링크)도 걷어낸 주소를 쓰게 한다
		if (isInitialized) ReactGA.gtag('set', { page_location: pageLocation })

		// 제목은 Helmet 이 다음 프레임에 바꾼다. 그 뒤에 읽어야 이전 페이지 제목이 붙지 않는다.
		window.requestAnimationFrame(() => {
			window.setTimeout(() => {
				if (!ensureReady()) return
				const params = { page_location: pageLocation, page_title: document.title }
				if (isInitialized) ReactGA.gtag('event', 'page_view', params)
				// GTM 의 「모든 페이지」는 첫 로드에만 돈다. 화면 이동은 이 이벤트로 잡는다 (ANALYTICS §11)
				pushGtm('page_view', params)
			}, 0)
		})
	},

	/**
	 * 동의 전·철회. clearCookies 는 명시적으로 거부했을 때만.
	 * GTM 이 올라와 있었으면 새로고침한다 — 업체 픽셀은 멈출 스위치가 없다. 다시 뜰 때는 동의가 없어 안 올라온다.
	 */
	disable: (clearCookies: boolean) => {
		if (MEASUREMENT_ID !== '') setMeasurementDisabled(true)
		if (clearCookies) removeGaCookies()
		if (isGtmLoaded) window.location.reload()
	},

	/** 화면 안에서 이동해 백오피스로 들어가면 GTM 을 새로고침으로 내린다. 경로가 바뀔 때마다 부른다 */
	unloadGtmOnExcludedPath: (pathname: string) => {
		if (isGtmLoaded && matchesPrefix(pathname, GTM_UNLOAD_PATH_LIST))
			window.location.reload()
	},

	/** 구매 화면 `/shop/{code}` 진입. 가격은 API 가 늦게 와서 싣지 않는다 */
	viewItem: (product: { code: string; name: string }) =>
		send('view_item', { items: [{ item_id: product.code, item_name: product.name }] }),

	addToCart: (anItem: AnalyticsItemType) =>
		send('add_to_cart', {
			currency: 'USD',
			value: round2(anItem.seasonUsd * anItem.qty),
			items: [toGaItem(anItem, true)],
		}),

	beginCheckout: (cartList: AnalyticsItemType[]) =>
		send('begin_checkout', {
			currency: 'USD',
			value: round2(
				cartList.reduce((sum, anItem) => sum + anItem.seasonUsd * anItem.qty, 0),
			),
			items: cartList.map(anItem => toGaItem(anItem, true)),
		}),

	/** 결제 완료. transactionId 가 같으면 GA 가 중복을 걸러낸다 */
	purchase: (order: {
		transactionId: string
		cartList: AnalyticsItemType[]
		value: number
		currency: string
		shipping?: number
		coupon?: string
		paymentType: 'paypal' | 'card' | 'card_krw'
	}) =>
		send('purchase', {
			transaction_id: order.transactionId,
			value: order.value,
			currency: order.currency,
			shipping: order.shipping,
			coupon: order.coupon,
			payment_type: order.paymentType,
			items: order.cartList.map(anItem => toGaItem(anItem, order.currency === 'USD')),
		}),

	/** Contact 전송 성공. topic = 문의 분류(categ) */
	lead: (topic: string) => send('generate_lead', { inquiry_topic: topic }),

	signUp: () => send('sign_up', { method: 'email' }),

	login: () => send('login', { method: 'email' }),

	/**
	 * 다운로드. 향상된 측정의 자동 `file_download` 와 겹치지 않게 이름을 따로 쓴다 —
	 * 자동 수집은 `.dmg`·blob PDF·스크립트로 여는 링크를 못 잡고, `.exe`·`.pdf` 링크는 잡는다.
	 */
	download: (name: AnalyticsDownloadEventType, params: Record<string, string>) =>
		send(name, params),

	/**
	 * 웹 매뉴얼 검색 — GA 권장 이벤트 `search`(`search_term` 은 GA 기본 측정기준).
	 * results = 0 인 검색어가 매뉴얼 보강 목록이 된다 (MANUAL_WEB_PLAN §4.6).
	 */
	manualSearch: (params: {
		searchTerm: string
		manualCode: string
		manualLanguage: string
		results: number
	}) =>
		send('search', {
			search_term: params.searchTerm,
			manual_code: params.manualCode,
			manual_language: params.manualLanguage,
			results: params.results,
		}),

	/** 웹 매뉴얼에서 목차·검색 결과로 절을 열었다. manual_section = `9.4.3` */
	manualSection: (params: {
		manualCode: string
		manualLanguage: string
		section: string
		method: 'toc' | 'search'
	}) =>
		send('manual_section', {
			manual_code: params.manualCode,
			manual_language: params.manualLanguage,
			manual_section: params.section,
			method: params.method,
		}),
}
